import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';

import type { Membership } from '@/auth/auth-context';
import { ROLE_LABELS } from '@/auth/roles';

interface ChooseOrganizationFormProps {
  memberships: Membership[];
  onChoose: (chosen: Membership) => void;
}

/**
 * Which organization to open, for somebody who belongs to more than one.
 *
 * Shown between the verification code and the dashboard. It asks rather than
 * picking, because the person may hold a different role in each and landing
 * them in the wrong one would silently show them the wrong menu.
 *
 * The role is shown beside each name for the same reason: "Sierra Club" and
 * "Sierra Club" are not enough to choose between when one of them is the one
 * you can actually administer.
 */
export function ChooseOrganizationForm({
  memberships,
  onChoose,
}: ChooseOrganizationFormProps) {
  return (
    <Stack spacing={2}>
      <List disablePadding aria-label="Your organizations">
        {memberships.map((membership) => (
          <ListItemButton
            key={membership.id}
            onClick={() => onChoose(membership)}
            divider
            sx={{ borderRadius: 1 }}
          >
            <ListItemText
              primary={membership.name}
              secondary={
                membership.status === 'ACTIVE'
                  ? ROLE_LABELS[membership.role]
                  : `${ROLE_LABELS[membership.role]} · ${membership.status.toLowerCase()}`
              }
            />
          </ListItemButton>
        ))}
      </List>

      {/*
        A keyboard user reaching the end of the list should not have to guess
        that there is nothing else here. The list items are the buttons; this
        only restates the first one for anybody who wants an obvious default.
      */}
      <Button variant="contained" fullWidth onClick={() => onChoose(memberships[0])}>
        Continue to {memberships[0].name}
      </Button>
    </Stack>
  );
}
